import React, { useEffect, useState } from 'react'
import { Book, Download, Eye, Mail, FileText, Calendar, Globe, Building, Hash, ChevronRight } from 'lucide-react'

interface BookSamplePreviewContentProps {
  data: {
    title?: string
    author?: string
    description?: string
    pdfUrl?: string
    coverUrl?: string
    emailGateEnabled?: boolean
    downloadEnabled?: boolean
    isbn?: string
    publisher?: string
    year?: string
    pages?: number
    language?: string
    genre?: string
    companyPosition?: 'top' | 'bottom'
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      branding?: string
      title?: string
      author?: string
      metadata?: string
      description?: string
    }
  }
  branding: {
    companyName?: string
    primaryColor?: string
    textColor?: string
    secondaryColor?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
  }
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean
}

export const BookSamplePreviewContent: React.FC<BookSamplePreviewContentProps> = ({ 
  data, 
  branding,
  colorMode = 'gradient',
  isPreview = true
}) => {
  const [showPDFModal, setShowPDFModal] = useState(false)

  // Load Google Fonts if needed
  useEffect(() => {
    const fonts = new Set([data.fonts?.heading, data.fonts?.body].filter(Boolean))
    fonts.forEach(font => {
      if (font && font !== 'Inter') {
        const link = document.createElement('link')
        link.href = `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}:wght@400;600;700&display=swap`
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
    })
  }, [data.fonts])

  // Helper function to determine text color based on background
  const getTextColorForBg = (bgColor: string): string => {
    const color = bgColor.replace('#', '')
    const r = parseInt(color.substr(0, 2), 16)
    const g = parseInt(color.substr(2, 2), 16)
    const b = parseInt(color.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness > 128 ? '#1F2937' : '#FFFFFF'
  }

  const primaryTextColor = colorMode === 'structured' 
    ? getTextColorForBg(branding.primaryColor || '#3B82F6')
    : (branding.textColor || '#FFFFFF')
  
  const secondaryTextColor = colorMode === 'structured'
    ? getTextColorForBg(branding.secondaryColor || '#1F2937')
    : (branding.textColor || '#FFFFFF')

  // Determine background based on color mode
  const backgroundStyle = colorMode === 'gradient' 
    ? { background: `linear-gradient(to bottom right, ${branding.primaryColor || '#3B82F6'}, ${branding.secondaryColor || '#1F2937'})` }
    : { backgroundColor: branding.primaryColor || '#3B82F6' }

  if (colorMode === 'structured') {
    // Structured mode with 65/35 split
    return (
      <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
        <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="min-h-full flex flex-col relative">
            {/* Header section - 65% */}
            <div 
              className="px-6 relative"
              style={{
                backgroundColor: branding.primaryColor,
                minHeight: '65%',
                paddingTop: '1rem',
                paddingBottom: '2rem'
              }}
            >
              {/* Content wrapper */}
              <div className="h-full flex flex-col">
                {/* Company name at top */}
                {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
                  <div className="text-center mb-4">
                    <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} 
                        style={{ color: primaryTextColor, fontFamily: data.fonts?.body || 'Inter' }}>
                      {branding.companyName}
                    </h2>
                  </div>
                )}

                {/* Book info with cover */}
                <div className="flex gap-4 mb-4">
                  {/* Book cover */}
                  <div className="flex-shrink-0">
                    {data.coverUrl ? (
                      <img 
                        src={data.coverUrl} 
                        alt={data.title}
                        className="w-24 h-32 rounded-lg shadow-lg object-cover"
                      />
                    ) : (
                      <div className="w-24 h-32 rounded-lg shadow-lg bg-gray-200 flex items-center justify-center">
                        <Book className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Book details */}
                  <div className="flex-1 min-w-0">
                    <h1 className={`${data.textSizes?.title || 'text-2xl'} font-bold mb-1 break-words`} 
                        style={{ 
                          color: primaryTextColor,
                          fontFamily: data.fonts?.heading || 'Inter'
                        }}>
                      {data.title || 'Buchtitel'}
                    </h1>
                    <p className={`${data.textSizes?.author || 'text-lg'} mb-2`}
                       style={{ 
                         color: primaryTextColor,
                         opacity: 0.9,
                         fontFamily: data.fonts?.body || 'Inter'
                       }}>
                      von {data.author || 'Autor'}
                    </p>
                    
                    {/* Metadata */}
                    <div className="space-y-1">
                      {data.genre && (
                        <p className={`${data.textSizes?.metadata || 'text-sm'}`}
                           style={{ color: primaryTextColor, opacity: 0.8, fontFamily: data.fonts?.body || 'Inter' }}>
                          {data.genre}
                        </p>
                      )}
                      {data.pages && (
                        <p className={`${data.textSizes?.metadata || 'text-sm'} flex items-center gap-1`}
                           style={{ color: primaryTextColor, opacity: 0.8, fontFamily: data.fonts?.body || 'Inter' }}>
                          <FileText className="w-3 h-3" />
                          {data.pages} Seiten
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {data.description && (
                  <div className="mb-4">
                    <p className={`${data.textSizes?.description || 'text-base'} leading-relaxed break-words whitespace-pre-wrap`}
                       style={{ 
                         color: primaryTextColor,
                         opacity: 0.9,
                         fontFamily: data.fonts?.body || 'Inter'
                       }}>
                      {data.description}
                    </p>
                  </div>
                )}

                {/* Spacer */}
                <div className="flex-1"></div>
              </div>
            </div>

            {/* Body section - 35% */}
            <div 
              className="flex-1 px-6 py-6"
              style={{
                backgroundColor: branding.secondaryColor,
                color: secondaryTextColor
              }}
            >
              <div className="max-w-sm mx-auto">
                {/* PDF Preview placeholder */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 mb-4 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-2 opacity-50" style={{ color: secondaryTextColor }} />
                  <p className={`${data.textSizes?.metadata || 'text-sm'}`} style={{ color: secondaryTextColor, opacity: 0.7 }}>
                    PDF Leseprobe
                  </p>
                </div>

                {/* Action buttons */}
                <div className="space-y-3">
                  {/* Hide email CTA in production; gate handled before rendering */}
                  {data.emailGateEnabled && isPreview ? (
                    <button 
                      className="w-full px-4 py-3 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105"
                      style={{ backgroundColor: branding.primaryColor, color: primaryTextColor }}
                      type="button"
                      onClick={() => {/* preview only, no-op */}}
                    >
                      <Mail className="w-5 h-5" />
                      <span className={data.textSizes?.metadata || 'text-sm'}>E-Mail eingeben & Leseprobe erhalten</span>
                    </button>
                  ) : (
                    <button 
                      className="w-full px-4 py-3 rounded-full font-medium flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105"
                      style={{ backgroundColor: branding.primaryColor, color: primaryTextColor }}
                      type="button"
                      onClick={() => {
                        if (data.pdfUrl) {
                          window.open(data.pdfUrl, '_blank', 'noopener,noreferrer')
                        }
                      }}
                    >
                      <Eye className="w-5 h-5" />
                      <span className={data.textSizes?.metadata || 'text-sm'}>Leseprobe ansehen</span>
                    </button>
                  )}

                  {data.downloadEnabled && (
                    <button 
                      className="w-full px-4 py-3 rounded-full font-medium flex items-center justify-center gap-2 border-2 transition-all hover:bg-white/10"
                      style={{ borderColor: secondaryTextColor, color: secondaryTextColor }}
                      type="button"
                      onClick={() => {
                        if (!data.pdfUrl) return
                        try {
                          const a = document.createElement('a')
                          a.href = data.pdfUrl
                          // Best-effort filename
                          a.download = (data.title?.replace(/\s+/g, '_') || 'leseprobe') + '.pdf'
                          document.body.appendChild(a)
                          a.click()
                          document.body.removeChild(a)
                        } catch {
                          window.open(data.pdfUrl, '_blank', 'noopener,noreferrer')
                        }
                      }}
                    >
                      <Download className="w-5 h-5" />
                      <span className={data.textSizes?.metadata || 'text-sm'}>PDF herunterladen</span>
                    </button>
                  )}
                </div>

                {/* Company name at bottom */}
                {branding.brandingType !== 'none' && (
                  <div className="text-center mt-6">
                    {branding.brandingType === 'beyondpages' ? (
                      <p className="text-sm opacity-60" style={{ color: secondaryTextColor, fontFamily: data.fonts?.body || 'Inter' }}>
                        Powered by BeyondPages
                      </p>
                    ) : branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'bottom' ? (
                      <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} 
                          style={{ color: secondaryTextColor, fontFamily: data.fonts?.body || 'Inter' }}>
                        {branding.companyName}
                      </h2>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Gradient mode (original implementation with enhancements)
  return (
    <div className={`min-h-screen relative ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`} style={backgroundStyle}>
      {/* Animated background particles - only for gradient mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      {/* Header with Branding */}
      <div className="relative z-10">
        {branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'top' && (
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center">
              <span className={`${data.textSizes?.branding || 'text-sm'} font-semibold`} 
                    style={{ color: branding.textColor || '#FFFFFF', fontFamily: data.fonts?.body || 'Inter' }}>
                {branding.companyName}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="relative z-10 px-4 py-4">
        {/* Book Info Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-4 mb-3">
          <div className="flex gap-3 mb-3">
            {data.coverUrl ? (
              <img 
                src={data.coverUrl} 
                alt={data.title}
                className="w-24 h-32 rounded-lg shadow-lg object-cover"
              />
            ) : (
              <div className="w-24 h-32 rounded-lg shadow-lg bg-gray-200 flex items-center justify-center">
                <Book className="w-10 h-10 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <h1 className={`${data.textSizes?.title || 'text-lg'} font-bold mb-1`}
                  style={{ 
                    color: 'white',
                    fontFamily: data.fonts?.heading || 'Inter'
                  }}>
                {data.title || 'Buchtitel'}
              </h1>
              <p className={`${data.textSizes?.author || 'text-sm'} mb-2 text-white/80`}
                 style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                von {data.author || 'Autor'}
              </p>
              
              {/* Book Details */}
              <div className="space-y-1">
                {data.publisher && (
                  <p className={`${data.textSizes?.metadata || 'text-xs'} text-white/70`}
                     style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                    {data.publisher}
                  </p>
                )}
                {data.year && (
                  <p className={`${data.textSizes?.metadata || 'text-xs'} flex items-center gap-1 text-white/70`}
                     style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                    <Calendar className="w-3 h-3" />
                    {data.year}
                  </p>
                )}
                {data.pages && (
                  <p className={`${data.textSizes?.metadata || 'text-xs'} flex items-center gap-1 text-white/70`}
                     style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                    <FileText className="w-3 h-3" />
                    {data.pages} Seiten
                  </p>
                )}
                {data.language && (
                  <p className={`${data.textSizes?.metadata || 'text-xs'} flex items-center gap-1 text-white/70`}
                     style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                    <Globe className="w-3 h-3" />
                    {data.language}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {data.description && (
            <div className="mb-3">
              <p className={`${data.textSizes?.description || 'text-sm'} leading-relaxed text-white/90 whitespace-pre-wrap`}
                 style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                {data.description}
              </p>
            </div>
          )}

          {/* Genre */}
          {data.genre && (
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-white/10 rounded-full text-xs text-white"
                    style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                {data.genre}
              </span>
            </div>
          )}
        </div>

        {/* Sample Preview Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-4">
          <h2 className={`${data.textSizes?.metadata || 'text-sm'} font-bold mb-3 flex items-center gap-2 text-white`}
              style={{ fontFamily: data.fonts?.heading || 'Inter' }}>
            <FileText className="w-4 h-4" />
            Leseprobe
          </h2>

          {/* PDF Preview Placeholder */}
          <div className="bg-white/5 rounded-lg p-8 mb-3 text-center">
            <FileText className="w-16 h-16 mx-auto mb-2 text-white/40" />
            <p className={`${data.textSizes?.metadata || 'text-sm'} text-white/60`}
               style={{ fontFamily: data.fonts?.body || 'Inter' }}>
              PDF Vorschau
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {data.emailGateEnabled && isPreview ? (
              <button 
                className="w-full px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 shadow-lg"
                style={{ backgroundColor: branding.primaryColor || '#3B82F6', color: branding.textColor || '#FFFFFF', fontFamily: data.fonts?.body || 'Inter' }}
                type="button"
              >
                <Mail className="w-3.5 h-3.5" />
                E-Mail eingeben & Leseprobe erhalten
              </button>
            ) : (
              <button 
                className="w-full px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 shadow-lg"
                style={{ backgroundColor: branding.primaryColor || '#3B82F6', color: branding.textColor || '#FFFFFF', fontFamily: data.fonts?.body || 'Inter' }}
                type="button"
                onClick={() => { if (data.pdfUrl) window.open(data.pdfUrl, '_blank', 'noopener,noreferrer') }}
              >
                <Eye className="w-3.5 h-3.5" />
                Leseprobe ansehen
              </button>
            )}

            {data.downloadEnabled && (
              <button 
                className="w-full px-3 py-2 rounded-full font-medium text-xs flex items-center justify-center gap-1.5 border border-white/20 text-white hover:bg-white/10"
                style={{ fontFamily: data.fonts?.body || 'Inter' }}
                type="button"
                onClick={() => {
                  if (!data.pdfUrl) return
                  try {
                    const a = document.createElement('a')
                    a.href = data.pdfUrl
                    a.download = (data.title?.replace(/\s+/g, '_') || 'leseprobe') + '.pdf'
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                  } catch {
                    window.open(data.pdfUrl, '_blank', 'noopener,noreferrer')
                  }
                }}
              >
                <Download className="w-3.5 h-3.5" />
                PDF herunterladen
              </button>
            )}
          </div>

          {/* Info Text */}
          <p className={`${data.textSizes?.metadata || 'text-xs'} text-center mt-3 text-white/60`}
             style={{ fontFamily: data.fonts?.body || 'Inter' }}>
            {data.emailGateEnabled 
              ? 'Ihre E-Mail wird nur für Updates zu diesem Buch verwendet.'
              : 'Kostenlose Leseprobe - keine Registrierung erforderlich.'}
          </p>
        </div>

        {/* Footer */}
        {branding.brandingType !== 'none' && (
          <div className="mt-3 text-center">
            {branding.brandingType === 'beyondpages' ? (
              <p className={`${data.textSizes?.metadata || 'text-xs'} text-white/60`}
                 style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                Powered by BeyondPages
              </p>
            ) : branding.brandingType === 'custom' && branding.companyName && data.companyPosition === 'bottom' ? (
              <p className={`${data.textSizes?.branding || 'text-sm'} font-semibold text-white/80`}
                 style={{ fontFamily: data.fonts?.body || 'Inter' }}>
                {branding.companyName}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
